/**
 * Drift timeline (doc 147 §6.6 E8 + PR Q3 scrubber + compare).
 *
 * v1 uses the entity's docRef number as a proxy for age (docs are
 * numbered roughly chronologically). A full git-blame-backed
 * implementation would pull manifest snapshots from `diffManifests`'
 * Cloud Storage history and diff them — v2 work.
 *
 * `buildDriftScrubber` + `compareDriftPoints` are PR Q3 additions —
 * pure helpers that turn the docRef proxy into scrubber-friendly ticks
 * and a between-anchors diff. The shape stays stable for the snapshot
 * swap; only the data source changes.
 */
import type { AtlasManifest } from '@atlas-manifest/types';

export interface DriftRow {
  docNum: number;
  entityIds: string[];
}

export function buildDriftTimeline(manifest: AtlasManifest): DriftRow[] {
  const byDoc = new Map<number, string[]>();
  for (const entity of manifest.entities) {
    const match = entity.docRef.match(/^(\d+)/);
    if (!match) continue;
    const num = Number(match[1]);
    const list = byDoc.get(num) ?? [];
    list.push(entity.id);
    byDoc.set(num, list);
  }
  return [...byDoc.entries()]
    .map(([docNum, entityIds]) => ({ docNum, entityIds: entityIds.sort() }))
    .sort((a, b) => a.docNum - b.docNum);
}

export interface DriftTick {
  /** Doc number (proxy for commit) at this anchor. */
  docNum: number;
  /** Synthetic stable sha derived from the manifest commit + docNum. */
  sha: string;
  /** Display label for the commit card (date-shaped). */
  label: string;
  /** Number of entities introduced at this anchor. */
  entityChangeCount: number;
  /** Entity ids introduced at this anchor (alphabetical). */
  entityIds: string[];
}

export interface DriftScrubber {
  ticks: DriftTick[];
  /** Range covered (for axis labels). */
  range: { firstDoc: number; lastDoc: number };
  /** True when the source is the docRef proxy (vs real snapshots). */
  fallback: boolean;
}

export function buildDriftScrubber(manifest: AtlasManifest): DriftScrubber {
  const timeline = buildDriftTimeline(manifest);
  if (timeline.length === 0) {
    return { ticks: [], range: { firstDoc: 0, lastDoc: 0 }, fallback: true };
  }
  const shaBase = manifest.generatedFromCommit || 'manifest';
  const ticks: DriftTick[] = timeline.map((row) => ({
    docNum: row.docNum,
    sha: `${shaBase.slice(0, 6)}.${row.docNum}`,
    label: `doc ${row.docNum}`,
    entityChangeCount: row.entityIds.length,
    entityIds: row.entityIds,
  }));
  return {
    ticks,
    range: {
      firstDoc: timeline[0]!.docNum,
      lastDoc: timeline[timeline.length - 1]!.docNum,
    },
    fallback: true,
  };
}

export interface DriftDiff {
  added: string[];
  removed: string[];
  /** Pair, lower-first, of the anchors used. */
  range: { fromDoc: number; toDoc: number };
}

export function compareDriftPoints(
  manifest: AtlasManifest,
  fromDoc: number,
  toDoc: number,
): DriftDiff {
  const lo = Math.min(fromDoc, toDoc);
  const hi = Math.max(fromDoc, toDoc);
  if (lo === hi) return { added: [], removed: [], range: { fromDoc: lo, toDoc: hi } };
  const timeline = buildDriftTimeline(manifest);
  const added: string[] = [];
  for (const row of timeline) {
    if (row.docNum > lo && row.docNum <= hi) added.push(...row.entityIds);
  }
  return { added, removed: [], range: { fromDoc: lo, toDoc: hi } };
}
